import { useCallback, useEffect, useMemo, useState } from "react";
import { ScrollView, StyleSheet } from "react-native";
import { ErrorModal, loaderRef } from "../../src/components";
import { AppStorage } from "../../src/services/AppStorage";

import {
  isMobileDevice,
  isWeb,
  phoneWithCountryCode,
  saveTokens,
  useLoginAuthenticateUserMutation,
  useLoginSendSmsCodeMutation,
} from "@stepapp/core";
import {
  Block,
  Button,
  OtpInput,
  Typography,
  palette,
  spacing,
  typographyVariants,
  useBreakpoints,
} from "@stepapp/ui";

import { useTranslation } from "react-i18next";
import { useRegisterFormContext } from "./_layout";

export default function RegisterOTPVerificationScreen() {
  const [timeLeft, setTimeLeft] = useState(45);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const { fullHdHeight } = useBreakpoints();
  const { t } = useTranslation();

  const disabled = useMemo(() => timeLeft > 0, [timeLeft]);

  const { setValue, watch } = useRegisterFormContext();

  const [loginAuthenticateUser] = useLoginAuthenticateUserMutation();
  const [loginSendSmsCode] = useLoginSendSmsCodeMutation();

  const phoneNumber = watch("phoneNumber");
  const countryCodePhoneNumber = phoneWithCountryCode(phoneNumber, AppStorage().get("countryCode"));

  useEffect(() => {
    let interval: Maybe<NodeJS.Timeout> = null;

    if (disabled) {
      // @ts-ignore: NodeJS.Timeout is not defined in the global scope
      interval = setInterval(() => setTimeLeft((timeLeft) => timeLeft - 1), 1000);
    } else {
      interval && clearInterval(interval);
    }

    return () => {
      interval && clearInterval(interval);
    };
  }, [disabled]);

  const handleSMSCodeSend = useCallback(async () => {
    loaderRef.current?.showLoader();

    await loginSendSmsCode({ variables: { phone_number: countryCodePhoneNumber } });

    loaderRef.current?.hideLoader();
  }, [loginSendSmsCode, countryCodePhoneNumber]);

  const handleSMSCodeVerification = useCallback(
    async (code: string) => {
      if (code?.length === 6) {
        loaderRef.current?.showLoader();
        const { data } = await loginAuthenticateUser({
          variables: { code, phone_number: countryCodePhoneNumber },
        });

        if (data?.solveLoginChallenge?.access_token) {
          setValue("code", code);
          setValue("otpCodeVerified", true);
          const { refresh_token, access_token } = data?.solveLoginChallenge || {};
          saveTokens({ accessToken: access_token, refreshToken: refresh_token, AppStorage });
        } else {
          loaderRef.current?.hideLoader();
          setTimeLeft(0);
          setErrorMsg("wrongSmsOrPhoneNumber");
          setValue("code", "");
          setValue("otpCodeVerified", false);
        }
      }
    },
    [setValue, countryCodePhoneNumber, loginAuthenticateUser],
  );

  const handleSendAgain = useCallback(() => {
    handleSMSCodeSend();
    setTimeLeft(30);
  }, [handleSMSCodeSend]);

  return (
    <ScrollView>
      <Typography variant={fullHdHeight ? "largeTitle" : "title"}>
        {t("screens.oneTimePasswordForm.title")}
      </Typography>

      <Block paddingTop={fullHdHeight ? 4 : 2}>
        <Typography type="secondary" variant="body">
          {t("screens.oneTimePasswordForm.description", { phoneNumber: countryCodePhoneNumber })}
        </Typography>
      </Block>

      <Block style={styles.formContainer}>
        <OtpInput handleSubmit={handleSMSCodeVerification} numberOfInputs={6} selfCenter />

        <Button
          disabled={disabled}
          label={
            disabled
              ? t("screens.oneTimePasswordForm.sendAgainIn", {
                  countdown: timeLeft,
                })
              : t("screens.oneTimePasswordForm.sendAgain")
          }
          onPress={handleSendAgain}
          style={styles.sendAgainButton}
          textStyle={[styles.sendAgain, disabled && styles.disabledText]}
          type="link"
        />
      </Block>

      <ErrorModal errorMsg={errorMsg} setErrorMsg={setErrorMsg} />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  disabledText: {
    ...typographyVariants.body,
    color: palette.font.placeholder,
  },
  formContainer: {
    paddingTop: spacing.unit * (isMobileDevice ? 5 : 7),
  },
  sendAgain: {
    ...typographyVariants.bodyBold,
    color: palette.primary.main,
    paddingBottom: isWeb ? 0 : spacing.unit * 6,
    paddingTop: isWeb ? 0 : spacing.unit * 4,
  },
  sendAgainButton: {
    marginTop: spacing.unit * (isWeb ? 7 : 4),
  },
});
